// ============================================================================
// Site Aggregate Service
// Composite operations that combine data from multiple services
// ============================================================================

import {
  Site,
  SiteWithDetails,
} from '../types/site';
import { fetchWithAuth } from './auth';

// Import from domain services
import { getSiteHookups } from './hookupService';
import { getSitePricing } from './pricingService';
import { getSiteOverageFees, getSiteCharges } from './feeService';
import { getSiteBlackouts } from './blackoutService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Internal Site Fetchers (to avoid circular imports)
// ============================================================================

async function fetchSite(siteId: number): Promise<Site> {
  return fetchWithAuth(`${API_BASE_URL}/sites/${siteId}`);
}

// ============================================================================
// Composite Operations
// ============================================================================

/**
 * Get site with all related details (hookups, pricing, fees, blackouts)
 * Used by SiteFormModal for editing sites
 */
export async function getSiteWithDetails(siteId: number): Promise<SiteWithDetails> {
  const [site, hookups, pricing, overageFees, charges, blackouts] = await Promise.all([
    fetchSite(siteId),
    getSiteHookups(siteId),
    getSitePricing(siteId),
    getSiteOverageFees(siteId),
    getSiteCharges(siteId),
    getSiteBlackouts(siteId),
  ]);

  return {
    ...site,
    hookups,
    pricing,
    overage_fees: overageFees,
    imposed_charges: charges,
    blackouts,
  };
}

// ============================================================================
// Named Export Object
// ============================================================================

export const siteAggregateService = {
  getSiteWithDetails,
};

export default siteAggregateService;
